import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { DeviceInfo } from '../../deviceInfo/deviceInfo';
import { Logger } from '../../logger/Logger';
import { useIsLargerThanWide } from '../../styles/hooks/media';
import { getWindowObject } from '../../utils/window';
import { toggleFullscreen } from '../gameRoot/useFullscreen';
import { useLegacyContext } from '../legacyMode/LegacyContextProvider';

import styles from './Fullscreen.module.css';

// Very Old Browsers and new iPads
interface WebkitDocument extends Document {
  webkitFullscreenEnabled?: boolean;
  webkitFullscreenElement?: HTMLElement;
  webkitExitFullscreen: () => Promise<void>;
}

const iOS_MinimalHeight = 25;

export const Fullscreen: FunctionComponent<{ isReadyToRender: boolean }> = ({ isReadyToRender }): ReactNode => {
  const { isLegacyMode } = useLegacyContext();
  const isWide = useIsLargerThanWide();

  const windowObject = getWindowObject();
  const fullscreenAvailable =
    windowObject.document.fullscreenEnabled || (windowObject.document as WebkitDocument).webkitFullscreenEnabled;

  const isAppleMobile = DeviceInfo.getPlatform().IS_IOS;
  const isOtherMobile = useRef(
    navigator.maxTouchPoints > 1 &&
      windowObject.matchMedia(
        '(max-device-width: 500px) and (max-device-height: 1000px), (max-device-width: 1000px) and (max-device-height: 500px)'
      ).matches
  );
  const [showFullscreen, setShowFullscreen] = useState<boolean>(false);

  useEffect(() => {
    if (!isWide && (isAppleMobile || isOtherMobile.current)) {
      const media = windowObject.matchMedia('(orientation: landscape)');

      const toggleFullScreenClass = (): void => {
        setTimeout(() => {
          const isFullScreenEnable = windowObject.screen.width - windowObject.innerHeight <= iOS_MinimalHeight;
          if (!isFullScreenEnable) {
            windowObject.document.documentElement.scrollTo(0, 0);
          }
          windowObject.document.documentElement.classList.toggle('ios-fullscreen', isFullScreenEnable);
        }, 350);
      };

      const mediaFunc = function ({ matches }: { matches: boolean }, isInitial: boolean = false): void {
        Logger.logEvent('rotate_' + isInitial, {
          additional: matches ? 'landscape' : 'portrait',
        });

        if (matches) {
          setShowFullscreen(true);
          if (isAppleMobile) {
            windowObject.document
              .querySelector<HTMLElement>('.' + styles.FullScreen)
              ?.style.setProperty('height', 'calc(100vh + 300px)');
            toggleFullScreenClass();
            windowObject.addEventListener('resize', toggleFullScreenClass);
          }
        } else {
          setShowFullscreen(false);
          if (isAppleMobile) {
            windowObject.document.querySelector<HTMLElement>('.' + styles.FullScreen)?.style.removeProperty('height');
            toggleFullScreenClass();
            windowObject.scrollTo(0, 1);
            windowObject.removeEventListener('resize', toggleFullScreenClass);
          } else if (
            windowObject.document.fullscreenElement ||
            (windowObject.document as WebkitDocument).webkitFullscreenElement
          ) {
            toggleFullscreen(isLegacyMode);
          }
        }
      };

      mediaFunc(media, true);
      media.addEventListener('change', mediaFunc);
      return (): void => {
        media.removeEventListener('change', mediaFunc);
        windowObject.removeEventListener('resize', toggleFullScreenClass);
      };
    }
  }, [isWide]);

  const handleClick = (): void => {
    if (isOtherMobile.current) {
      toggleFullscreen(isLegacyMode);
    }
  };

  if (isWide) return null;
  if (isOtherMobile.current && fullscreenAvailable === false) return null;
  if (isAppleMobile && windowObject !== windowObject.parent) return null;

  return (
    <div className={styles.FullScreen} style={{ zIndex: showFullscreen ? undefined : -999 }} onClick={handleClick}>
      {isReadyToRender ? <div className={`${styles.inner} ${isAppleMobile ? styles.swipeUp : styles.click}`} /> : null}
    </div>
  );
};
